import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRealtimeConnection, useRealtimeStateStore } from "./useConnectionState";
import {
    RealtimeEvents,
    WorkItemAddedNotificationSchema,
    WorkItemCompletedNotificationSchema,
    WorkItemDueNotificationSchema,
    WorkItemSnoozedNotificationSchema,
    WorkItemLockedNotificationSchema,
    WorkItemUnlockedNotificationSchema,
    type WorkItemAddedNotification,
    type WorkItemCompletedNotification,
    type WorkItemDueNotification,
    type WorkItemSnoozedNotification,
    type WorkItemLockedNotification,
    type WorkItemUnlockedNotification,
} from "./notifications";
import {
    patchWorkItemCompletedInQueueLists,
    patchWorkItemSnoozedInQueueLists,
    patchWorkItemLockedInQueueLists,
    patchWorkItemUnlockedInQueueLists,
} from "./cachePatches";
import { createInvalidationCoalescer } from "./invalidationCoalescer";
import { watchRealtimeLifecycle } from "./realtimeLifecycle";

// ---------------------------------------------------------------------------
// Cache surface
//
// The realtime layer reaches into TanStack Query by key-prefix only —
// it never imports feature hooks. The prefixes are the boundary
// contract; when a feature changes its keys, this list is the single
// update point.
//
// Slice 6 introduced an asymmetric reconciliation strategy
// (`docs/REALTIME.md`):
//
//   * `WorkItemCompleted` carries every field a completion changes,
//     so we patch the cached entries directly via `setQueriesData`
//     (see `cachePatches.ts`). The follow-on REST refetch is skipped
//     because the patch is authoritative for the visible delta.
//
//   * `WorkItemAdded` carries only identifiers; the cache patch would
//     have to invent `body`, `createdBy`, etc. so we keep the existing
//     invalidate-and-refetch path. Users perceive the round trip as
//     "the empty space filled in" — a much weaker latency cost than
//     the "completed item should be crossed out but isn't" jank a
//     missing patch on the completion path produces.
//
// `["queues", "tallies"]` MUST stay in lock-step with `queueKeys.tallies`
// in `experience-shell/queues/hooks.ts` — the consumer is
// `useQueueWorkItemTallies`, which renders the sidebar Open/Pending
// badges. Both events invalidate this prefix because the wire payload
// doesn't carry a count delta the patch helpers could apply; the hook
// refetches the authoritative number. A behavioral regression test
// (`useQueueRealtime.test.tsx`) seeds the actual tally key and asserts
// the invalidation lands, so the next silent rename of the queues
// feature key fails CI instead of degrading badges to stale-only.
// ---------------------------------------------------------------------------

const QUEUE_TALLIES_KEY_PREFIX = ["queues", "tallies"] as const;
const WORK_ITEMS_BY_QUEUE_KEY_PREFIX = ["workitems", "byQueue"] as const;
const WORK_ITEMS_MINE_KEY_PREFIX = ["workitems", "mine"] as const;

function safeParseAdded(payload: unknown): WorkItemAddedNotification | null {
    const parsed = WorkItemAddedNotificationSchema.safeParse(payload);
    return parsed.success ? parsed.data : null;
}

function safeParseCompleted(payload: unknown): WorkItemCompletedNotification | null {
    const parsed = WorkItemCompletedNotificationSchema.safeParse(payload);
    return parsed.success ? parsed.data : null;
}

function safeParseDue(payload: unknown): WorkItemDueNotification | null {
    const parsed = WorkItemDueNotificationSchema.safeParse(payload);
    return parsed.success ? parsed.data : null;
}

function safeParseSnoozed(payload: unknown): WorkItemSnoozedNotification | null {
    const parsed = WorkItemSnoozedNotificationSchema.safeParse(payload);
    return parsed.success ? parsed.data : null;
}

function safeParseLocked(payload: unknown): WorkItemLockedNotification | null {
    const parsed = WorkItemLockedNotificationSchema.safeParse(payload);
    return parsed.success ? parsed.data : null;
}

function safeParseUnlocked(payload: unknown): WorkItemUnlockedNotification | null {
    const parsed = WorkItemUnlockedNotificationSchema.safeParse(payload);
    return parsed.success ? parsed.data : null;
}

/**
 * Subscribes the active TanStack QueryClient to realtime queue events.
 *
 * Effect contract:
 *   - On `WorkItemAdded`: invalidate the queue tally and per-queue /
 *     "mine" work item list key prefixes so the next render refetches.
 *     Invalidation is used (not a patch) because the notification does
 *     not carry the full work item shape.
 *   - On `WorkItemCompleted`: invalidate the queue tally prefix (count
 *     deltas need a refetch) and patch the per-queue / "mine" list
 *     caches in place — the patch removes the row from open-only lists
 *     and updates completion fields on history lists. No per-queue list
 *     refetch fires. The `["workitems","mine"]` prefix is additionally
 *     invalidated so the taskbar App Badge's deduplicated count refetches
 *     its authoritative total (the in-place patch only decrements
 *     `totalCount` for a row present on a cached page, which the badge's
 *     single-row query does not hold).
 *   - On `WorkItemDue`: the time-driven Pending -> Open crossing. Same
 *     invalidate-and-refetch shape as `WorkItemAdded` (the notification
 *     carries no count delta) — the Open badge ticks up and the listing
 *     order can shift, so the tally (when awaiting-action is non-empty)
 *     and the list prefixes refetch.
 *   - On a gap (the hub is not connected): invalidate the tally prefix and
 *     the per-queue / "mine" list prefixes every ~15 s on a visible tab, and
 *     once after the hub reconnects (`watchRealtimeLifecycle`). Pushes sent
 *     during the gap are lost, so this refetches what they would have
 *     updated. While the hub is connected no timer runs: a dropped push is
 *     picked up by a later push, or by the next window focus once the data
 *     is 15 s stale.
 *   - On unmount: detach event handlers and stop the gap watcher — leaving
 *     the handlers attached would leak cache mutations after the user
 *     navigates away from the queue workspace.
 *
 * Mount this hook once in the queue-bearing layout (currently
 * `WorkflowLayout`). Compositions never call it directly; the
 * `compositions → tanstack` arrow is preserved.
 */
export function useQueueRealtime(): void {
    // Queue groups are joined server-side on connect, so there is no join to
    // redo. The hook binds handlers once per connection object. It reads the
    // connection state only through the store (no React state), so a state
    // tick costs no render.
    const connection = useRealtimeConnection();
    const stateStore = useRealtimeStateStore();
    const queryClient = useQueryClient();

    useEffect(() => {
        if (connection === null) return;

        // One refetch per key per burst. Patches below stay synchronous.
        const invalidation = createInvalidationCoalescer(queryClient);

        const handleAdded = (payload: unknown): void => {
            const notification = safeParseAdded(payload);
            if (notification === null) return;
            // Tallies are driven by awaitingActionByQueueIds — a reply
            // (empty awaiting list) can't move any badge, so skip the
            // tally refetch and save the round trip. The listing /
            // timeline caches still invalidate because visibility
            // changes (the reply must show up in the audience queues'
            // chat-style timelines).
            if (notification.awaitingActionByQueueIds.length > 0) {
                invalidation.invalidate(QUEUE_TALLIES_KEY_PREFIX);
            }
            invalidation.invalidate(WORK_ITEMS_BY_QUEUE_KEY_PREFIX);
            invalidation.invalidate(WORK_ITEMS_MINE_KEY_PREFIX);
        };

        const handleCompleted = (payload: unknown): void => {
            const notification = safeParseCompleted(payload);
            if (notification === null) return;
            // Patch first, then invalidate tallies. Order doesn't matter
            // functionally (different cache slots) but reading the
            // patch ahead of the invalidate matches the user's mental
            // model: "the row updates immediately; the badge refetches".
            patchWorkItemCompletedInQueueLists(queryClient, notification);
            invalidation.invalidate(QUEUE_TALLIES_KEY_PREFIX);
            // Completed history is visibility-based and the newly completed
            // item may not have existed on any cached history page. Refetch
            // queue lists so page 1 and totalCount include it immediately.
            invalidation.invalidate(WORK_ITEMS_BY_QUEUE_KEY_PREFIX);
            // The taskbar App Badge reads the deduplicated /workitems/mine
            // total via a single-row query, so the in-place completion
            // patch above can't decrement it (the completed row isn't on
            // that page). Invalidate the "mine" prefix so the badge's
            // authoritative total refetches and the badge clears as the
            // user catches up.
            invalidation.invalidate(WORK_ITEMS_MINE_KEY_PREFIX);
        };

        const handleDue = (payload: unknown): void => {
            const notification = safeParseDue(payload);
            if (notification === null) return;
            // A due crossing flips the item Pending -> Open, so the Open
            // badge ticks up: invalidate the tally cache to refetch the
            // authoritative count (the notification carries no count delta).
            // Guard on awaiting-action so an item owed to nobody can't move a
            // badge. The listing caches invalidate too because the bucket
            // flip can reorder the row within a queue's worklist.
            if (notification.awaitingActionByQueueIds.length > 0) {
                invalidation.invalidate(QUEUE_TALLIES_KEY_PREFIX);
            }
            invalidation.invalidate(WORK_ITEMS_BY_QUEUE_KEY_PREFIX);
            invalidation.invalidate(WORK_ITEMS_MINE_KEY_PREFIX);
        };

        const handleSnoozed = (payload: unknown): void => {
            const notification = safeParseSnoozed(payload);
            if (notification === null) return;
            // A snooze can flip an item between the Open and Pending
            // buckets (WorkItemTally.Classify pivots on `DueDate < now`),
            // so the tally has to refetch — the notification doesn't
            // carry a count delta. Patch the row's `dueDate` first so
            // the listing UI reflects the new value without a flash.
            patchWorkItemSnoozedInQueueLists(queryClient, notification);
            if (notification.awaitingActionByQueueIds.length > 0) {
                invalidation.invalidate(QUEUE_TALLIES_KEY_PREFIX);
            }
        };

        const handleLocked = (payload: unknown): void => {
            const notification = safeParseLocked(payload);
            if (notification === null) return;
            // A lock annotates a row in place; it never moves a tally
            // (locks carry no awaiting-action semantics) and never adds or
            // removes a list row, so only the list caches patch.
            patchWorkItemLockedInQueueLists(queryClient, notification);
        };

        const handleUnlocked = (payload: unknown): void => {
            const notification = safeParseUnlocked(payload);
            if (notification === null) return;
            patchWorkItemUnlockedInQueueLists(queryClient, notification);
        };

        connection.on(RealtimeEvents.workItemAdded, handleAdded);
        connection.on(RealtimeEvents.workItemCompleted, handleCompleted);
        connection.on(RealtimeEvents.workItemDue, handleDue);
        connection.on(RealtimeEvents.workItemSnoozed, handleSnoozed);
        connection.on(RealtimeEvents.workItemLocked, handleLocked);
        connection.on(RealtimeEvents.workItemUnlocked, handleUnlocked);

        // Gap recovery. Pushes sent while the hub was down are lost, so
        // refetch what this hook keeps fresh: every ~15 s while the hub is
        // down (visible tab) and once after it reconnects. Nothing runs on a
        // timer while the hub is up.
        const stopLifecycle = watchRealtimeLifecycle(stateStore, {
            onResync: () => {
                invalidation.invalidate(QUEUE_TALLIES_KEY_PREFIX);
                invalidation.invalidate(WORK_ITEMS_BY_QUEUE_KEY_PREFIX);
                invalidation.invalidate(WORK_ITEMS_MINE_KEY_PREFIX);
            },
        });

        return () => {
            stopLifecycle();
            invalidation.dispose();
            connection.off(RealtimeEvents.workItemAdded, handleAdded);
            connection.off(RealtimeEvents.workItemCompleted, handleCompleted);
            connection.off(RealtimeEvents.workItemDue, handleDue);
            connection.off(RealtimeEvents.workItemSnoozed, handleSnoozed);
            connection.off(RealtimeEvents.workItemLocked, handleLocked);
            connection.off(RealtimeEvents.workItemUnlocked, handleUnlocked);
        };
    }, [connection, stateStore, queryClient]);
}
